import { useTranslation } from "next-i18next";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Button, Listbox } from "@talladega/components";
import { ArrowRightIcon, MapPinIcon } from "@talladega/icons";
import { ChangeEvent, useEffect, useState } from "react";
import GoogleMapsWrapper from "@modules/location/helpers/GoogleMapsWrapper";
import LocationMap from "@modules/locations/components/individual/LocationMap";
import MapLocation from "@modules/locations/types/MapLocation";
import DropOffLocation from "@modules/drop-off-location/types/DropOffLocation";
import { gtmPushData } from "@talladega/helpers";
import useUser from "@modules/user/hooks/useUser";
import { Purchase } from "@modules/purchase/types/Purchase";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import {
  convertDealerLocationToMapLocation,
  findClosestLocation,
  sortLocationsBasedOnProximity,
} from "@common/helpers/distanceMatrixHelper";

type Props = {
  dropOffLocations: DropOffLocation[];
  onNextClick: (selectedDropOffLocation: DropOffLocation) => void;
  onDropOffLocationChange?: (dropOffLocation: DropOffLocation) => void;
  defaultDropOffLocation?: DropOffLocation;
  showDropdown?: boolean;
  latitude?: number | null;
  longitude?: number | null;
};

type DropOffLocationFormInput = {
  dropOffLocation: MapLocation;
};

export const getClosestLocationForLatLong = (
  dropOffLocations: DropOffLocation[],
  lat: number,
  long: number
) => {
  const closestLocation = findClosestLocation(
    lat,
    long,
    dropOffLocations
      .filter((d) => d.latitude && d.longitude)
      .map((d) => ({
        id: d.id,
        isShown: true,
        name: d.name,
        address: {
          address: "",
          address2: "",
          city: "",
          province: "",
          postalCode: "",
          latitude: d.latitude,
          longitude: d.longitude,
        },
      }))
  );

  return dropOffLocations.find((x) => x.name === closestLocation?.name);
};

export const getClosestLocationForPurchase = (
  dropOffLocations: DropOffLocation[],
  purchase: Purchase
) => {
  if (purchase.dropOffDetails?.dropOffLocationId) {
    return dropOffLocations.find(
      (x) => x.id === purchase.dropOffDetails?.dropOffLocationId
    );
  }

  const mainApplicant = purchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  if (
    mainApplicant?.currentAddress?.latitude &&
    mainApplicant?.currentAddress?.longitude
  ) {
    return getClosestLocationForLatLong(
      dropOffLocations,
      mainApplicant.currentAddress.latitude,
      mainApplicant.currentAddress.longitude
    );
  }

  return undefined;
};

const PickDropOffLocation = ({
  dropOffLocations,
  onNextClick,
  onDropOffLocationChange,
  defaultDropOffLocation,
  showDropdown = true,
  latitude,
  longitude,
}: Props) => {
  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<DropOffLocationFormInput>({
    defaultValues: {
      dropOffLocation: defaultDropOffLocation,
    },
  });
  const { t } = useTranslation(["purchase", "common"]);

  let locations: MapLocation[] = dropOffLocations.map((dl) =>
    convertDealerLocationToMapLocation(dl)
  );

  if (latitude && longitude) {
    locations = sortLocationsBasedOnProximity(latitude, longitude, locations);
  }

  const { user } = useUser();

  const [selectedDropOffLocation, setSelectedDropOffLocation] = useState<
    MapLocation | undefined
  >(
    defaultDropOffLocation
      ? convertDealerLocationToMapLocation(defaultDropOffLocation)
      : undefined
  );

  const onSubmit: SubmitHandler<DropOffLocationFormInput> = (
    form: DropOffLocationFormInput
  ) => {
    gtmPushData({
      // only works as long as this is only used for ICO
      event: "ICO",
      description: "ICO Select Drop Off Location",
      user_id: user?.id,
    });

    const dropOffLocation = dropOffLocations.find(
      (x) => x.id === form.dropOffLocation.id
    );

    if (!dropOffLocation)
      throw new Error("drop off location selected could not be found");

    onNextClick(dropOffLocation);
  };

  const onDropOffLocationSelectChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedDropOffLocation(event.target.value as unknown as MapLocation);
  };

  useEffect(() => {
    if (!defaultDropOffLocation) return;
    const mapLocation = convertDealerLocationToMapLocation(
      defaultDropOffLocation
    );

    setSelectedDropOffLocation(mapLocation);
    setValue("dropOffLocation", mapLocation);
  }, [defaultDropOffLocation, setValue]);

  const customRenderOption = (item: MapLocation) => {
    return (
      <div className="flex justify-between w-full gap-2">
        <div className="flex items-center">
          <MapPinIcon className="text-primary-bold h-5 w-5" />
          <span className="pl-2">{`${item.name}`}</span>
        </div>
        {item.distance ? (
          <div className="flex w-24 items-center text-center rounded-3xl py-1 px-1 text-xs bg-secondary-alt border border-secondary text-tertiary shrink-0 grow-0 self-start">
            <span className="text-center w-full">
              {t("purchase:kms_other", {
                formattedCount: Math.round(item.distance),
              })}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 items-start">
      {showDropdown && (
        <>
          <p className="text-lg text-secondary font-normal">
            {t("purchase:select_convenient_drop_off_location_message")}
          </p>

          <form
            className="w-full flex items-end justify-between"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="grow shrink-0 basis-0">
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("common:required"),
                  },
                  onChange: onDropOffLocationSelectChange,
                }}
                defaultValue={selectedDropOffLocation}
                name="dropOffLocation"
                render={({ field: { onChange, ref } }) => (
                  <Listbox
                    items={locations}
                    label={t("common:location")}
                    keyOption={(item) => item.id}
                    displayOption={(item) =>
                      item
                        ? item.name +
                          (item?.distance
                            ? ` - ${Math.round(item.distance)} km away`
                            : "")
                        : ""
                    }
                    selectedItem={selectedDropOffLocation}
                    renderOption={customRenderOption}
                    displayButton={(item) =>
                      item?.name != null
                        ? item.name
                        : t("common:select_location")
                    }
                    feedback={errors.dropOffLocation?.message}
                    valueOption={(item) => item}
                    onChange={(item) => {
                      const selectedDropOff = dropOffLocations.find(
                        (x) => x.id === item.id
                      );

                      if (!selectedDropOff) {
                        throw new Error(
                          `Selected drop off location not found in locations.`
                        );
                      }

                      onChange(item);
                      setSelectedDropOffLocation(item);
                      onDropOffLocationChange?.(selectedDropOff);
                    }}
                    status={errors.dropOffLocation ? "error" : "default"}
                    defaultButtonClassName="text-slate-500"
                    buttonRef={ref}
                  />
                )}
              />
            </div>
          </form>
        </>
      )}

      {selectedDropOffLocation && (
        <>
          <div className="row-span-full col-start-1 col-span-12 w-full self-center h-[20rem] rounded-xl overflow-hidden">
            <GoogleMapsWrapper>
              <LocationMap location={selectedDropOffLocation} />
            </GoogleMapsWrapper>
          </div>
          <div>
            <h5 className="font-normal">{selectedDropOffLocation.name}</h5>
            <p className="font-normal text-quaternary">
              {selectedDropOffLocation.address.address}{" "}
              {selectedDropOffLocation.address.city}{" "}
              {selectedDropOffLocation.address.province}
            </p>
          </div>
        </>
      )}
      <Button
        className="mt-6"
        rightIcon={<ArrowRightIcon className="h-5 w-5" />}
        onClick={handleSubmit(onSubmit)}
        disabled={!selectedDropOffLocation}
      >
        {t("common:next")}
      </Button>
    </div>
  );
};

export default PickDropOffLocation;
